import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone, MessageCircle, Mail, MapPin, Clock, ChevronDown, Headset } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { RoundLogo } from "../components/shared/RoundLogo";
import { BrandLogo } from "../components/shared/BrandLogo";

// ---------------------------------------------------------------------------
// EDIT HERE — business contact details shown on the public Support page.
// These mirror the in-app Help button; change them in one place if they move.
// ---------------------------------------------------------------------------
const CONTACT = {
  phoneDisplay: "+1 825-777-2325",
  phoneTel: "+18257772325",
  whatsappNumber: "18257772325", // no "+"
  email: "support@thevirsatiffin.com",
  address: "9609 107A Ave, Edmonton, Alberta T5H0Z6",
  hours: "Mon – Sat · 9:00 AM – 7:00 PM (Mountain Time)",
};

const WA_URL = `https://wa.me/${CONTACT.whatsappNumber}`;

const CONTACT_CARDS = [
  {
    key: "call", label: "Call us", value: CONTACT.phoneDisplay,
    Icon: Phone, href: `tel:${CONTACT.phoneTel}`, external: false,
  },
  {
    key: "whatsapp", label: "WhatsApp", value: "Chat or call on WhatsApp",
    Icon: MessageCircle, href: WA_URL, external: true,
  },
  {
    key: "email", label: "Email", value: CONTACT.email,
    Icon: Mail, href: `mailto:${CONTACT.email}`, external: false,
  },
];

const FAQS = [
  {
    q: "How do I pause a delivery?",
    a: "Open the app, go to your subscription and request a pause before 10:00 PM (Mountain Time) on the day before the delivery. Requests after the cutoff may not apply to the next day. A paused tiffin is not used — it carries forward to a future delivery date.",
  },
  {
    q: "What time will my tiffin arrive?",
    a: "Delivery times are estimates and can change with traffic, weather, route order and access. We make every effort to deliver within your scheduled window, but an exact arrival time can't be guaranteed.",
  },
  {
    q: "How many tiffins come with a subscription?",
    a: "A standard subscription includes 25 tiffins, unless your purchased plan states a different quantity. Each tiffin is one scheduled meal delivery.",
  },
  {
    q: "Do subscriptions renew automatically?",
    a: "No. Subscriptions never auto-renew and we never charge you for a new plan on our own. You can request a renewal from the app when you have three or fewer tiffins left; the new plan's deliveries begin after your current tiffins are used.",
  },
  {
    q: "Can I get a refund?",
    a: "Eligible unused tiffins may be refunded after a review of your remaining entitlement and any applicable charges. Contact us and our team will review your account. Refunds are processed to an available payment method.",
  },
  {
    q: "Do you accommodate food allergies?",
    a: "Our meals are prepared in a shared kitchen where allergen cross-contact may occur. We do not accommodate food allergies or guarantee allergen-free meals. If you have food allergies, we strongly recommend not consuming our food.",
  },
  {
    q: "How do I update my address or contact details?",
    a: "Update them from the Settings tab in the app, or contact us and we'll help. Address changes are subject to our service area and operational approval.",
  },
];

function FaqItem({ q, a, index }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-border last:border-b-0" data-testid={`support-faq-${index}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 py-4 text-left"
        data-testid={`support-faq-toggle-${index}`}
        aria-expanded={open}
      >
        <span className="text-sm font-semibold text-text-main">{q}</span>
        <ChevronDown
          size={18}
          strokeWidth={2}
          className={`shrink-0 text-text-muted transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <p className="pb-4 text-sm leading-relaxed text-text-muted" data-testid={`support-faq-answer-${index}`}>
          {a}
        </p>
      )}
    </div>
  );
}

export default function Support() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Support · The Virsa Tiffin";
    return () => { document.title = prevTitle; };
  }, []);

  return (
    <div className="min-h-[100dvh] bg-background" data-testid="support-page">
      <header
        className="flex items-center justify-center border-b border-white/10 bg-primary px-4 py-4"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 1rem)" }}
        data-testid="support-header"
      >
        <BrandLogo className="h-9" testId="support-header-logo" />
      </header>

      <main className="mx-auto w-full max-w-2xl px-4 py-8 pb-16" data-testid="support-main">
        <div className="mb-8 flex flex-col items-center text-center">
          <RoundLogo className="mb-4 h-16 w-16" />
          <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            <Headset size={14} strokeWidth={2} /> Support
          </span>
          <h1 className="mt-3 font-heading text-3xl font-semibold text-text-main">How can we help?</h1>
          <p className="mt-2 max-w-md text-sm text-text-muted">
            Reach The Virsa Tiffin team directly, or find quick answers to common questions below.
          </p>
        </div>

        {/* Contact cards */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3" data-testid="support-contact-cards">
          {CONTACT_CARDS.map(({ key, label, value, Icon, href, external }) => (
            <a
              key={key}
              href={href}
              target={external ? "_blank" : undefined}
              rel={external ? "noopener noreferrer" : undefined}
              className="group flex flex-col gap-2 rounded-2xl border border-border bg-surface p-4 transition-colors hover:border-primary/40 hover:bg-primary/5"
              data-testid={`support-contact-${key}`}
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                <Icon size={18} strokeWidth={1.75} />
              </span>
              <span className="text-sm font-semibold text-text-main">{label}</span>
              <span className="break-words text-xs text-text-muted">{value}</span>
            </a>
          ))}
        </div>

        {/* Address + hours */}
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="flex items-start gap-3 rounded-2xl border border-border bg-surface p-4" data-testid="support-address">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <MapPin size={18} strokeWidth={1.75} />
            </span>
            <div>
              <p className="text-sm font-semibold text-text-main">Visit us</p>
              <p className="mt-0.5 text-xs text-text-muted">{CONTACT.address}</p>
            </div>
          </div>
          <div className="flex items-start gap-3 rounded-2xl border border-border bg-surface p-4" data-testid="support-hours">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Clock size={18} strokeWidth={1.75} />
            </span>
            <div>
              <p className="text-sm font-semibold text-text-main">Support hours</p>
              <p className="mt-0.5 text-xs text-text-muted">{CONTACT.hours}</p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <section className="mt-10" data-testid="support-faq-section">
          <h2 className="mb-2 font-heading text-lg font-semibold text-text-main">Frequently asked questions</h2>
          <Card>
            <CardContent className="px-5 py-1">
              {FAQS.map((f, i) => (
                <FaqItem key={i} q={f.q} a={f.a} index={i} />
              ))}
            </CardContent>
          </Card>
        </section>

        <footer className="mt-10 text-center" data-testid="support-footer">
          <p className="text-xs text-text-muted">
            Still need help? Message us on{" "}
            <a href={WA_URL} target="_blank" rel="noopener noreferrer" className="font-semibold text-primary hover:underline" data-testid="support-footer-whatsapp">
              WhatsApp
            </a>{" "}
            and we'll get back to you.
          </p>
          <Link to="/customer" className="mt-3 inline-block text-xs font-semibold text-primary hover:underline" data-testid="support-back-to-app">
            Back to the app
          </Link>
          <p className="mt-4 text-[11px] text-text-muted/70">
            © {new Date().getFullYear()} The Virsa Tiffin · Edmonton, Alberta, Canada
          </p>
        </footer>
      </main>
    </div>
  );
}
